/**
 * Actions and pages — the half of the palette that never touches the network.
 *
 * Matched synchronously on every keystroke. That is the whole reason this file
 * exists separately from `searchConsole`: the most predictable rows in the
 * palette must also be the fastest, and a "give credit" row that arrives 180ms
 * after the operator typed "giv" has already lost the race against their hands.
 *
 * Everything here ROUTES. Nothing mutates. A palette is a place you pass
 * through at speed with a virtual cursor you may have moved by accident, so an
 * ⏎ on the wrong row must never move money — it must land you on the screen that
 * asks you to confirm it.
 */

import { SEARCH_SCORE, compareHits, matchField, type SearchHit } from '@/data/api'
import { ROLE_LABEL, creatableRoles, type Account, type Role } from '@/data/types'
import { ALL_NAV_ITEMS } from '@/app/nav'
import type { IconName } from '@w7/ui'

/** What the palette knows about the signed-in operator when it ranks actions. */
export interface ActionContext {
  /** The signed-in account's role. Gates the create rows to what they may create. */
  role?: Role
  /**
   * DIRECT children only. Directive 4 is absolute — give and take apply to
   * direct children and nothing else — so a deeper account produces NO give/take
   * row rather than a disabled one. A disabled row in a palette is unreachable
   * noise that still costs a ↓ press.
   */
  children?: Pick<Account, 'id' | 'username' | 'displayName' | 'role'>[]
}

export interface ActionDef {
  id: string
  /** The verb phrase. Weighted as a primary field, like a username. */
  label: string
  /** What it does — shown as the row's subtitle, matched at tertiary weight. */
  subtitle: string
  /**
   * The vocabulary of the product being replaced. An operator will type "net
   * exposure", "account statement", "view member" or "monday" — the keywords are
   * how the old words still find the new screen.
   */
  keywords: string[]
  icon: IconName
  to: string
  when?: (ctx: ActionContext) => boolean
}

const canCreate = (ctx: ActionContext, role: Role): boolean =>
  Boolean(ctx.role && creatableRoles(ctx.role).includes(role))

/**
 * The static catalog.
 *
 * `Add an agent` and `Add a member` both land on the one-page create form — the
 * form owns role selection, and a `?role=` param it does not read would be a
 * link that lies. They are still two rows because which of them is OFFERED is
 * real information: a Master sees only "Add a member", because that is all a
 * Master may create.
 */
export const ACTIONS: ActionDef[] = [
  {
    id: 'action:create',
    label: 'Add to downline',
    subtitle: 'Create an agent or a member under yourself',
    keywords: ['create', 'new', 'add', 'register', 'signup', 'open account'],
    icon: 'plus',
    to: '/create',
  },
  {
    id: 'action:create-agent',
    label: 'Add an agent',
    subtitle: 'Create the next tier down and give it its own downline',
    keywords: ['create agent', 'new master', 'add master', 'super master', 'sub agent'],
    icon: 'users',
    to: '/create',
    when: (ctx) => Boolean(ctx.role && creatableRoles(ctx.role).some((r) => r !== 'player')),
  },
  {
    id: 'action:create-member',
    label: 'Add a member',
    subtitle: 'Create a member who bets against your book',
    keywords: ['create member', 'new user', 'add player', 'add client'],
    icon: 'user',
    to: '/create',
    when: (ctx) => canCreate(ctx, 'player'),
  },
  {
    id: 'action:bulk-settle',
    label: 'Bulk settle outstanding',
    subtitle: 'Take every selected account to a zero settlement in one run',
    keywords: ['bulk', 'settle', 'monday', 'zero', 'clear', 'transfer', 'settlement'],
    icon: 'wallet',
    to: '/downline?settle=1',
  },
  {
    id: 'action:my-pnl',
    label: 'My P&L',
    subtitle: 'What the book earned you, after shares and commission',
    keywords: ['profit', 'loss', 'earnings', 'my profit and loss', 'pl'],
    icon: 'pnl',
    to: '/reports/my-pnl',
  },
  {
    id: 'action:member-pnl',
    label: 'Member P&L',
    subtitle: 'Who is winning and losing beneath you',
    keywords: ['user pnl', 'player pnl', 'member profit', 'downline pl'],
    icon: 'users',
    to: '/reports/member-pnl',
  },
  {
    id: 'action:turnover',
    label: 'Turnover',
    subtitle: 'Matched volume by product, sport and agent',
    keywords: ['volume', 'matched', 'stake', 'business'],
    icon: 'turnover',
    to: '/reports/turnover',
  },
  {
    id: 'action:commission',
    label: 'Commission',
    subtitle: 'Commission earned and paid down the chain',
    keywords: ['comm', 'rate', 'brokerage'],
    icon: 'commission',
    to: '/reports/commission',
  },
  {
    id: 'action:transfers',
    label: 'Credit transfers',
    subtitle: 'Every give and take, with running balances',
    keywords: ['statement', 'account statement', 'transfer', 'ref', 'ledger', 'give', 'take'],
    icon: 'transfers',
    to: '/reports/transfers',
  },
  {
    id: 'action:exposure',
    label: 'Exposure',
    subtitle: 'Worst-case liability across the open book',
    keywords: ['net exposure', 'book', 'liability', 'risk', 'position'],
    icon: 'exposure',
    to: '/exposure',
  },
  {
    id: 'action:downline',
    label: 'Downline',
    subtitle: 'Every agent and member beneath you',
    keywords: ['members', 'agents', 'tree', 'hierarchy', 'view member', 'list'],
    icon: 'downline',
    to: '/downline',
  },
  {
    id: 'action:activity',
    label: 'Activity',
    subtitle: 'Who did what, when, from where',
    keywords: ['logs', 'audit', 'all logs', 'trail', 'history'],
    icon: 'activity',
    to: '/activity',
  },
  {
    id: 'action:2fa',
    label: 'Two-factor authentication',
    subtitle: 'Add or remove a second factor on your sign-in',
    keywords: ['2fa', 'mfa', 'google authenticator', 'otp', 'security', 'passkey'],
    icon: 'shield',
    to: '/settings/security',
  },
]

/**
 * Score one action against the needle.
 *
 * Field weights mirror the data-layer ladder exactly: the verb phrase is a
 * primary field like a username, the old product's vocabulary is secondary, and
 * the explanatory subtitle is tertiary. Highlight ranges are emitted only for a
 * LABEL match — marking the label at offsets computed against a keyword would
 * paint the highlight in the wrong place, which is worse than none.
 */
function scoreAction(def: ActionDef, needle: string): { score: number; ranges: [number, number][] } | null {
  let best: { score: number; ranges: [number, number][] } | null = null

  const consider = (score: number, ranges: [number, number][]) => {
    if (!best || score > best.score) best = { score, ranges }
  }

  const label = matchField(def.label, needle)
  if (label) {
    consider(
      SEARCH_SCORE.base.action + SEARCH_SCORE.quality[label.quality] * SEARCH_SCORE.weight.primary,
      label.ranges,
    )
  }
  for (const keyword of def.keywords) {
    const hit = matchField(keyword, needle)
    if (!hit) continue
    consider(
      SEARCH_SCORE.base.action + SEARCH_SCORE.quality[hit.quality] * SEARCH_SCORE.weight.secondary,
      [],
    )
  }
  const subtitle = matchField(def.subtitle, needle)
  if (subtitle) {
    consider(
      SEARCH_SCORE.base.action +
        SEARCH_SCORE.quality[subtitle.quality] * SEARCH_SCORE.weight.tertiary,
      [],
    )
  }

  return best
}

/**
 * Quality good enough to conjure the templated give/take/statement/bets rows.
 *
 * A subsequence hit on a username must not: "abc" matches half the downline by
 * construction, and four action rows per accidental match would bury the four
 * real answers underneath them.
 */
const TEMPLATE_QUALITIES = new Set(['exact', 'prefix', 'word-prefix'])

/**
 * The contextual rows, for the ONE direct child the operator most likely means.
 *
 * One child, not every match: four rows each across three near-miss usernames is
 * twelve rows of speculation above the account results that actually answer the
 * question. The best match by username quality is the one they typed at.
 */
function templatedActions(ctx: ActionContext, needle: string): SearchHit[] {
  const children = ctx.children ?? []
  let best: { child: Pick<Account, 'id' | 'username' | 'role'>; score: number; ranges: [number, number][] } | null =
    null

  for (const child of children) {
    const match = matchField(child.username, needle)
    if (!match || !TEMPLATE_QUALITIES.has(match.quality)) continue
    const score =
      SEARCH_SCORE.base.action + SEARCH_SCORE.quality[match.quality] * SEARCH_SCORE.weight.primary
    if (best && score <= best.score) continue
    best = { child, score, ranges: match.ranges }
  }

  if (!best) return []
  const { child, score, ranges } = best
  const who = child.username
  const tier = ROLE_LABEL[child.role]
  // Ranges are computed against the username, so they are mapped onto the
  // offset at which the username appears inside each row's title.
  const shift = (offset: number): [number, number][] =>
    ranges.map(([s, e]) => [s + offset, e + offset] as [number, number])

  const rows: { id: string; title: string; subtitle: string; icon: IconName; to: string; bonus: number }[] = [
    {
      id: `action:give:${child.id}`,
      title: `Give credit to ${who}`,
      subtitle: `Move credit down to this ${tier} — opens the transfer, nothing moves yet`,
      icon: 'give',
      to: `/downline?q=${encodeURIComponent(who)}&transfer=give&account=${encodeURIComponent(child.id)}`,
      bonus: 3,
    },
    {
      id: `action:take:${child.id}`,
      title: `Take credit from ${who}`,
      subtitle: `Pull credit back from this ${tier} — opens the transfer, nothing moves yet`,
      icon: 'take',
      to: `/downline?q=${encodeURIComponent(who)}&transfer=take&account=${encodeURIComponent(child.id)}`,
      bonus: 2,
    },
    {
      id: `action:statement:${child.id}`,
      title: `Open ${who}'s statement`,
      subtitle: 'Every give and take with this account, with running balances',
      icon: 'transfers',
      to: `/member/${encodeURIComponent(child.id)}?tab=statement`,
      bonus: 1,
    },
    {
      id: `action:bets:${child.id}`,
      title: `Open ${who}'s bets`,
      subtitle: 'The bet log for this account',
      icon: 'exposure',
      to: `/member/${encodeURIComponent(child.id)}?tab=bets`,
      bonus: 0,
    },
  ]

  return rows.map((row) => ({
    id: row.id,
    kind: 'action' as const,
    title: row.title,
    subtitle: row.subtitle,
    ranges: shift(row.title.indexOf(who)),
    to: row.to,
    icon: row.icon,
    // The bonus only orders these four against each other; it is far too small
    // to lift an action above the account row for the same person, which is
    // correct — the person outranks the paperwork about the person.
    score: score + row.bonus,
  }))
}

/**
 * Every action matching `text`, ranked on the shared ladder and never capped.
 *
 * Uncapped because there are at most a dozen and no "see all actions" page to
 * send anyone to; the cap exists to make a 41-row account list honest, not to
 * ration a fixed menu.
 */
export function matchActions(text: string, ctx: ActionContext = {}): SearchHit[] {
  const needle = text.trim()
  if (!needle) return []

  const hits: SearchHit[] = []
  for (const def of ACTIONS) {
    if (def.when && !def.when(ctx)) continue
    const scored = scoreAction(def, needle)
    if (!scored) continue
    hits.push({
      id: def.id,
      kind: 'action',
      title: def.label,
      subtitle: def.subtitle,
      ranges: scored.ranges,
      to: def.to,
      icon: def.icon,
      score: scored.score,
    })
  }

  hits.push(...templatedActions(ctx, needle))
  return hits.sort(compareHits)
}

/**
 * The empty-state "Jump to" list, generated from the nav model.
 *
 * Generated, not hand-listed: a new rail entry becomes searchable with no second
 * edit, which is the only way a duplicated list stays in sync for longer than a
 * quarter.
 */
export function navHits(): SearchHit[] {
  return ALL_NAV_ITEMS.map((item) => ({
    id: `nav:${item.id}`,
    kind: 'action' as const,
    title: item.label,
    subtitle: item.description,
    ranges: [],
    to: item.to,
    icon: item.icon,
    score: SEARCH_SCORE.base.action,
  }))
}

/** The five actions offered before anything is typed. */
export function topActions(ctx: ActionContext = {}): SearchHit[] {
  return ACTIONS.filter((def) => !def.when || def.when(ctx))
    .slice(0, 5)
    .map((def) => ({
      id: def.id,
      kind: 'action' as const,
      title: def.label,
      subtitle: def.subtitle,
      ranges: [],
      to: def.to,
      icon: def.icon,
      score: SEARCH_SCORE.base.action,
    }))
}
